import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Eye, Loader2, Lock, ArrowLeft } from 'lucide-react';
import { API_GUIDE, PDF_GUIDE, TEINTES, criteresDe, idsDe, telecharger } from '../components/guide-bc/constantes';
import { useGuideProgression } from '../components/guide-bc/useGuideProgression';
import { EnteteGuide } from '../components/guide-bc/EnteteGuide';
import { OngletMethode } from '../components/guide-bc/OngletMethode';
import { OngletBloc } from '../components/guide-bc/OngletBloc';
import { OngletSynthese } from '../components/guide-bc/OngletSynthese';
import { SuiviGuide } from '../components/guide-bc/SuiviGuide';
import { PilotageGuide } from '../components/guide-bc/PilotageGuide';
import { nomComplet, wordStagiaire } from '../components/guide-bc/suiviCommun';

const Onglets = ({ guide, acces, onglet, setOnglet, progression }) => {
  const items = [['methode', 'Méthode', null], ...guide.blocs.map((b) => [b.code, b.code, b]), ['synthese', 'Synthèse', null],
    ...(acces.equipe ? [['suivi', 'Suivi des stagiaires', null]] : []), ...(acces.pilote ? [['pilotage', 'Pilotage', null]] : [])];
  return (
    <nav className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-md" data-testid="guide-onglets">
      <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 py-2 sm:px-6">
        {items.map(([k, l, b]) => {
          const actif = onglet === k;
          const c = b && progression.compte(idsDe(criteresDe(b)));
          return (
            <button key={k} type="button" onClick={() => setOnglet(k)} data-testid={`guide-onglet-${k.toLowerCase()}`} aria-pressed={actif}
              className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${actif ? (b ? `${TEINTES[b.couleur].fort} text-white` : 'bg-[#0F172A] text-white') : 'text-slate-600 hover:bg-slate-100'}`}>
              {l}{c && <span className={`rounded-full px-1.5 text-[11px] ${actif ? 'bg-white/25' : 'bg-slate-100 text-slate-500'}`}>{c.pret}/{c.total}</span>}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

const BandeauLecture = ({ vue, onQuitter }) => (
  <div className="flex flex-wrap items-center justify-center gap-3 bg-[#0E7C74] px-4 py-2 text-sm text-white" data-testid="guide-bandeau-lecture">
    <Eye className="h-4 w-4" /> Lecture seule : guide de <b data-testid="guide-lecture-nom">{nomComplet(vue)}</b>{vue.session && ` · session ${vue.session}`}
    <button type="button" onClick={onQuitter} data-testid="guide-quitter-lecture"
      className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/30"><ArrowLeft className="h-3.5 w-3.5" /> Revenir à mon guide</button>
  </div>
);

const Ferme = ({ acces }) => (
  <div className="mx-auto mt-16 max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="guide-ferme">
    <Lock className="mx-auto h-10 w-10 text-slate-300" />
    <h1 className="mt-4 text-xl font-bold text-[#0F172A]">Le guide BC1 / BC2 n'est pas ouvert</h1>
    <p className="mt-2 text-sm text-slate-500">{acces.equipe ? "Le centre a fermé l'accès de l'équipe pédagogique." : 'Le centre l\'ouvrira au bon moment de votre formation.'}</p>
  </div>
);

const Contenu = ({ guide, acces }) => {
  const [params] = useSearchParams();
  const demande = params.get('onglet');
  const [onglet, setOnglet] = useState((demande === 'suivi' && acces.equipe) || (demande === 'pilotage' && acces.pilote) ? demande : 'methode');
  const [cible, setCible] = useState(null);
  const [vue, setVue] = useState(null);
  const progression = useGuideProgression(vue?.user_id, acces.equipe);
  const global = useMemo(() => progression.compte(guide.blocs.flatMap((b) => idsDe(criteresDe(b)))), [guide, progression]);
  const choisir = (k) => { setOnglet(k); setCible(null); window.scrollTo({ top: (document.getElementById('guide-contenu')?.offsetTop || 0) - 60, behavior: 'smooth' }); };
  const aller = (bloc, cle) => { setOnglet(bloc); setCible({ cle, t: Date.now() }); };
  const voir = (s) => { setVue(s); setOnglet('synthese'); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const reinitialiser = () => { if (window.confirm('Effacer toute votre progression et vos exemples ?')) progression.reinitialiser(); };
  const word = (b) => (vue ? wordStagiaire(vue, b) : telecharger(`/guide-bpjeps/ma-progression/word?bloc=${b}`, `Mon_brouillon_${b}.docx`));
  const bloc = guide.blocs.find((b) => b.code === onglet);

  return (
    <div className="-m-4 min-h-screen bg-[#F8FAFC] sm:-m-6" data-testid="guide-bc-page">
      {vue && <BandeauLecture vue={vue} onQuitter={() => setVue(null)} />}
      <EnteteGuide guide={guide} global={global} progression={progression} equipe={acces.equipe} onReinitialiser={reinitialiser} onWord={word}
        onPdf={() => telecharger(PDF_GUIDE, 'Guide_BC1_BC2_BPJEPS_Motonautisme.pdf')} />
      <Onglets guide={guide} acces={acces} onglet={onglet} setOnglet={choisir} progression={progression} />
      <main id="guide-contenu" className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        {!progression.charge && <Loader2 className="mx-auto h-7 w-7 animate-spin text-slate-400" />}
        {progression.charge && onglet === 'methode' && <OngletMethode guide={guide} />}
        {progression.charge && bloc && <OngletBloc key={bloc.code} bloc={bloc} progression={progression} cible={cible} />}
        {progression.charge && onglet === 'synthese' && <OngletSynthese guide={guide} progression={progression} onAller={aller} />}
        {onglet === 'suivi' && <SuiviGuide onVoir={voir} />}
        {onglet === 'pilotage' && <PilotageGuide />}
      </main>
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">FISL · {guide.reference} · Exemples fictifs</footer>
    </div>
  );
};

export default function GuideBC() {
  const [acces, setAcces] = useState(null);
  const [guide, setGuide] = useState(null);
  const [erreur, setErreur] = useState(false);
  useEffect(() => {
    axios.get('/guide-bpjeps/acces').then(({ data }) => {
      setAcces(data);
      return data.acces ? axios.get(API_GUIDE).then(({ data: g }) => setGuide(g)) : null;
    }).catch(() => setErreur(true));
  }, []);

  if (erreur) return <p className="p-10 text-center text-red-600" data-testid="guide-erreur">Impossible de charger le guide. Réessayez dans un instant.</p>;
  if (acces && !acces.acces) return <Ferme acces={acces} />;
  if (!guide) return <div className="flex min-h-[60vh] items-center justify-center text-slate-400"><Loader2 className="h-7 w-7 animate-spin" /></div>;
  return <Contenu guide={guide} acces={acces} />;
}
